// 编辑区域和滤镜滚动内容分别接受 JSON 留白。
import { contentPadding } from '../layout';
/**
 * L2 media — ScanEditor: the post-capture EDIT surface.
 *
 * What it is, and why it is the kit's rather than the host's or the worker's:
 * a rotate and an enhance filter are a DRAWING, not a document. "Make this
 * grayscale" has one honest latency — the moment the thumb leaves the tile —
 * and any round-trip through a session to get it is the wrong answer to the
 * wrong question. So everything here is instant and client-side: the picture
 * turns under a CSS transform, the filter is a CSS `filter:` string, and the
 * only thing that ever leaves this component is what CONFIRM emits.
 *
 * THE CAPABILITY GATE is the other half of that bargain and the reason
 * `filters` is a prop rather than a fixed strip. A blueprint names the enhance
 * set it WANTS; this kit intersects that against `SUPPORTED_SCAN_FILTERS` — the
 * set it can actually apply — and draws only the intersection. A filter this
 * build cannot do is HIDDEN, never a dead control, which is what lets the heavy
 * ML filters (de-shadow, de-handwrite, dewarp, HD) appear the day a kit
 * implements them with no blueprint change, and stay absent until then. The web
 * kit and the Flutter kit therefore agree on the SIGNAL, not on the strip.
 *
 * `original` is not in the requested set and is always drawn: it is not a
 * filter, it is the way BACK from one, and a strip you cannot leave is a trap.
 *
 * WHAT CONFIRM SENDS. `confirmed {images, edits}` — the pages, each baked
 * through a `<canvas>` that applies this page's rotation and filter for real,
 * plus the edit state (`[{page, rotate, filter}]`) so the receiver can see what
 * was done rather than infer it. A page nobody touched is passed through as its
 * own ref: re-encoding an untouched scan would cost the user quality for
 * nothing. And where the canvas cannot answer — jsdom, a picture the browser
 * has not decoded, a tainted cross-origin frame — the ORIGINAL ref rides
 * instead. Degrading to the unprocessed page is a smaller lie than throwing on
 * the one button the whole surface exists for.
 *
 * AND IT SENDS IT ONCE. Confirm is terminal, but the editor does not disappear
 * on the tap — it is still on screen while the receiver acts on what it was
 * handed, and one scan filed twice is two documents. So the terminal pair is
 * latched: whichever of Confirm/Cancel fires first spends both, synchronously,
 * before any baking.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { TYPE_STYLE, resetButton, hitTarget, toSlides, sourceLadder } from './ui';

/**
 * The enhance filters THIS kit can apply, as CSS filter strings.
 *
 * Deliberately the trivial four plus the identity: each one is a single-pass
 * colour transform a browser (and `ColorFilter.matrix` on the Flutter twin) can
 * do at 60fps, which is the whole definition of "the kit owns it". Anything
 * that needs a model does not belong in this table — it belongs in the worker,
 * and until a kit can do it the strip simply does not offer it.
 */
export const SCAN_FILTER_CSS = Object.freeze({
  original: 'none',
  gray: 'grayscale(1)',
  bw: 'grayscale(1) contrast(1.6) brightness(1.05)',
  auto: 'contrast(1.15) brightness(1.05)',
  contrast: 'contrast(1.3)',
});

/** The set this build can apply — the left-hand side of the capability gate. */
export const SUPPORTED_SCAN_FILTERS = Object.freeze(Object.keys(SCAN_FILTER_CSS));

/** What a page asks for when it names no set of its own (v1's full enhance set). */
export const DEFAULT_SCAN_FILTERS = Object.freeze(['gray', 'bw', 'auto', 'contrast']);

const FILTER_LABEL = {
  original: 'Original',
  gray: 'Gray',
  bw: 'B&W',
  auto: 'Auto',
  contrast: 'Contrast',
};

/** The identity edit — a page nobody has touched. */
const CLEAN = { rotate: 0, filter: 'original' };

const isClean = (e) => (e.rotate % 360 === 0) && (!e.filter || e.filter === 'original');

/**
 * Bake one page: draw it into a canvas with its rotation and filter applied and
 * return a data URL — or `null` when this runtime cannot, in which case the
 * caller passes the original ref through.
 *
 * Synchronous ON PURPOSE. The `<img>` is already in the DOM (every page is
 * mounted; only the current one is displayed), so by the time Confirm is tapped
 * the browser has decoded it and there is nothing to await. An async bake would
 * hang wherever images never load — which is every test runtime — and a Confirm
 * that never fires is worse than one that files the page unprocessed.
 */
function bakePage(img, { rotate, filter }) {
  try {
    const w = img?.naturalWidth || 0;
    const h = img?.naturalHeight || 0;
    if (!w || !h) return null;
    const turned = (((rotate % 360) + 360) % 360) % 180 !== 0;
    const canvas = document.createElement('canvas');
    canvas.width = turned ? h : w;
    canvas.height = turned ? w : h;
    const ctx = canvas.getContext?.('2d');
    if (!ctx || typeof ctx.drawImage !== 'function') return null;
    ctx.filter = SCAN_FILTER_CSS[filter] || 'none';
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
    return canvas.toDataURL('image/jpeg', 0.92);
  } catch {
    // A tainted canvas (cross-origin scan, no CORS header) throws on
    // `toDataURL`. The page is still the page — send the ref.
    return null;
  }
}

export function ScanEditor({ props = {}, data, emit }) {
  const pages = useMemo(() => {
    const raw = props.images !== undefined ? props.images : data;
    return toSlides(raw, { sources: sourceLadder(props.sources), labelField: props.slideLabel });
  }, [props.images, props.sources, props.slideLabel, data]);

  // ONE strip for the whole editor, computed once: the page's request, gated by
  // what this build can do, with `original` pinned at the head.
  const strip = useMemo(() => {
    const wanted = Array.isArray(props.filters) ? props.filters : DEFAULT_SCAN_FILTERS;
    const gated = wanted
      .map((f) => String(f))
      .filter((f) => f !== 'original' && SUPPORTED_SCAN_FILTERS.includes(f));
    return ['original', ...new Set(gated)];
  }, [props.filters]);

  const [index, setIndex] = useState(0);
  // Per PAGE, because rotation is: a scan of a stapled report is two portrait
  // pages and one landscape table, and one shared angle would be wrong for two
  // of them.
  const [edits, setEdits] = useState({});
  const imgs = useRef(new Map());

  /**
   * THE TERMINAL LATCH. Confirm hands the scan over and the editor STAYS
   * MOUNTED while the receiver opens what it just asked for — so a second tap
   * on a button that is still on screen is a second document out of one scan.
   * Confirm and Cancel are therefore at-most-once, together: whichever fires
   * first spends both.
   *
   * The REF is the guard that actually holds, and it goes down BEFORE the bake,
   * so a tap that lands in the same frame as the first finds it already down —
   * a latch that lived only in state would be read stale by everything that
   * beats the re-render. The state exists for one thing: to draw the buttons
   * spent, so the user can see why the second tap did nothing.
   *
   * It is keyed on THE PAGES, not on the mount: a fresh scan handed to the same
   * editor is a thing not yet filed, and would otherwise inherit a latch it
   * never earned — a Confirm that is dead on arrival.
   */
  const filed = useRef(null);
  const [spent, setSpent] = useState(null);
  const scan = pages.map((p) => p.src).join('\u0000');
  const latched = spent === scan;

  const cur = Math.min(index, Math.max(0, pages.length - 1));
  const edit = edits[cur] || CLEAN;

  const patch = useCallback((i, next) => {
    setEdits((prev) => ({ ...prev, [i]: { ...(prev[i] || CLEAN), ...next } }));
  }, []);

  const confirm = useCallback(() => {
    if (filed.current === scan) return;
    filed.current = scan; // before the bake — the frame's second tap is a no-op
    setSpent(scan);
    const state = pages.map((_, i) => edits[i] || CLEAN);
    const images = pages.map((p, i) => {
      if (isClean(state[i])) return p.src;
      return bakePage(imgs.current.get(i), state[i]) || p.src;
    });
    emit?.('confirmed', {
      images,
      edits: state.map((e, i) => ({ page: i, rotate: e.rotate, filter: e.filter })),
    });
  }, [pages, edits, emit, scan]);

  const cancel = useCallback(() => {
    if (filed.current === scan) return;
    filed.current = scan;
    setSpent(scan);
    emit?.('canceled', {});
  }, [emit, scan]);

  // 空态与图片共用四边留白；边距在舞台外，保持图片自身的宽高比。
  const bodyPadding = contentPadding(props.bodyPadding);
  const stageStyle = {
    width: `calc(100% - ${bodyPadding.paddingLeft + bodyPadding.paddingRight}px)`,
    marginLeft: bodyPadding.paddingLeft,
    marginTop: bodyPadding.paddingTop,
    marginBottom: bodyPadding.paddingBottom,
    aspectRatio: '3 / 4',
  };

  // NO PAGES IS NOT AN ERROR. The edit page is reached with the images the host
  // just captured, and a studio preview (or a first paint before the host has
  // handed anything over) legitimately has none yet. Saying so quietly is the
  // whole behaviour — never a throw, never a half-drawn stage.
  if (!pages.length) {
    return (
      <div style={{
        ...stageStyle,
        borderRadius: 'var(--r)', background: 'var(--surface-3)',
        border: '1px dashed var(--line)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 'var(--sp-2)', color: 'var(--text-3)',
      }}>
        <span aria-hidden style={{ fontSize: 28 }}>▣</span>
        <span style={{ ...TYPE_STYLE.caption }}>{props.emptyLabel || 'No pages yet'}</span>
      </div>
    );
  }

  const move = (delta) => setIndex((i) => {
    const at = Math.min(i, pages.length - 1);
    return (at + delta + pages.length) % pages.length;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', minWidth: 0 }}>
      {/* THE PAGE, and every other page with it. All of them are mounted so the
          browser has decoded them by the time Confirm bakes — the alternative
          is an async bake, and an async bake is a Confirm that can hang. Only
          the current one is displayed. */}
      <div style={{
        position: 'relative', ...stageStyle,
        borderRadius: 'var(--r)', overflow: 'hidden', background: 'var(--surface-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {pages.map((p, i) => {
          const e = edits[i] || CLEAN;
          return (
            <img
              key={p.src}
              ref={(el) => { if (el) imgs.current.set(i, el); else imgs.current.delete(i); }}
              src={p.src}
              alt={p.label || `Page ${i + 1}`}
              // NO `crossOrigin`, deliberately. It would let the canvas read
              // the pixels of a cross-origin scan — and it would also make the
              // browser REFUSE to display any scan whose signed URL carries no
              // CORS header, which is most of them. A page that files
              // unprocessed is a smaller loss than a page that never appears,
              // so the tainted-canvas throw is caught in `bakePage` instead.
              style={{
                display: i === cur ? 'block' : 'none',
                maxWidth: '100%', maxHeight: '100%',
                objectFit: 'contain',
                transform: `rotate(${e.rotate}deg)`,
                filter: SCAN_FILTER_CSS[e.filter] || 'none',
                transition: 'transform 180ms var(--ease), filter 120ms var(--ease)',
              }}
            />
          );
        })}
        {pages.length > 1 ? (
          <>
            <button type="button" aria-label="Previous page" onClick={() => move(-1)} style={pager('left')}>◀</button>
            <button type="button" aria-label="Next page" onClick={() => move(1)} style={pager('right')}>▶</button>
            <span style={{
              position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
              padding: '2px 10px', borderRadius: 'var(--r-pill)',
              background: 'rgba(0,0,0,0.45)', color: '#fff',
              fontSize: 'var(--t-xs)', fontWeight: 700,
            }}>{`${cur + 1} / ${pages.length}`}</span>
          </>
        ) : null}
      </div>

      {/* ROTATE — a transform the kit owns outright, in 90° steps, because a
          scanner that hands back a sideways page and no way to turn it has
          asked the user to tilt their head. */}
      <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', ...bodyPadding }}>
        <button
          type="button"
          aria-label="Rotate page"
          onClick={() => patch(cur, { rotate: (edit.rotate + 90) % 360 })}
          style={{
            ...resetButton, ...hitTarget,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '0 var(--sp-3)', borderRadius: 'var(--r-pill)',
            border: '1px solid var(--line)', background: 'var(--surface-2)',
            color: 'var(--text-2)', fontSize: 'var(--t-sm)', fontWeight: 600,
          }}
        >
          <span aria-hidden>↻</span>
          {props.rotateLabel || 'Rotate'}
        </button>
      </div>

      {/* THE FILTER STRIP — only what this build can actually apply. */}
      <div
        role="group"
        aria-label="Enhance"
        style={{ display: 'flex', gap: 'var(--sp-2)', overflowX: 'auto', scrollbarWidth: 'none', ...contentPadding(props.contentPadding) }}
      >
        {strip.map((f) => {
          const on = (edit.filter || 'original') === f;
          return (
            <button
              key={f}
              type="button"
              aria-pressed={on}
              onClick={() => patch(cur, { filter: f })}
              style={{
                ...resetButton, ...hitTarget,
                flex: '0 0 auto', minWidth: 0,
                padding: '0 var(--sp-3)', borderRadius: 'var(--r-pill)',
                border: `1px solid ${on ? 'var(--accent-line)' : 'var(--line)'}`,
                background: on ? 'var(--accent-soft)' : 'var(--surface-2)',
                color: on ? 'var(--accent)' : 'var(--text-2)',
                fontSize: 'var(--t-sm)', fontWeight: on ? 700 : 600,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 120ms var(--ease), color 120ms var(--ease)',
              }}
            >{FILTER_LABEL[f] || f}</button>
          );
        })}
      </div>

      {/* CONFIRM is the loud one and CANCEL is quiet, because backing out of an
          edit page costs the user the scan they just took. Both read spent once
          either has fired — the editor is on its way out and there is nothing
          left here to press. */}
      <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', ...bodyPadding }}>
        <button
          type="button"
          disabled={latched}
          onClick={confirm}
          style={{
            ...resetButton, ...hitTarget,
            flex: 1, padding: '0 var(--sp-4)', borderRadius: 'var(--r-pill)',
            background: 'var(--accent)', color: 'var(--accent-ink)',
            fontSize: 'var(--t-md)', fontWeight: 700,
            opacity: latched ? 0.4 : 1,
            cursor: latched ? 'default' : 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >{props.confirmLabel || 'Confirm'}</button>
        <button
          type="button"
          disabled={latched}
          onClick={cancel}
          style={{
            ...resetButton, ...hitTarget,
            padding: '0 var(--sp-4)', borderRadius: 'var(--r-pill)',
            color: 'var(--text-3)', fontSize: 'var(--t-sm)', fontWeight: 600,
            opacity: latched ? 0.4 : 1,
            cursor: latched ? 'default' : 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >{props.cancelLabel || 'Cancel'}</button>
      </div>
    </div>
  );
}

function pager(side) {
  return {
    ...resetButton,
    position: 'absolute', top: '50%', [side]: 8, transform: 'translateY(-50%)',
    width: 30, height: 30, borderRadius: 15, background: 'rgba(0,0,0,0.35)',
    color: '#fff', fontSize: 13,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}
